import { RGBA } from '@bpd-library/types';
import { getAssetsFolder } from '@utilities/get-assets-folder';
import {
    BLUE,
    BLUE_ALPHA,
    BLUE_ALPHA_LIGHT,
    GREEN,
    GREEN_ALPHA,
    GREY_DARK,
    ORANGE,
    ORANGE_ALPHA,
    PURPLE,
    PURPLE_ALPHA_LIGHT,
    RED,
    RED_ALPHA,
    RGB,
    WHITE,
    WHITE_ALPHA,
} from '@utilities/map';
import { nextEvent } from '@utilities/next-event';

import { CanvasStyle, CanvasStyleItem } from './types';

const STRIPED_IMAGE_URL = '/images/no-filter-found-pattern.png';
let stripedImage: HTMLImageElement;

const BASE_ITEM_STYLE: Partial<CanvasStyleItem> = {
    lineJoin: 'round',
    lineWidth: 2,
    lineCap: 'round',
    setLineDash: () => [],
};

const createColoredStyle = (strokeColor: RGB, fillColor: RGBA) => {
    return {
        ...BASE_ITEM_STYLE,
        strokeStyle: `rgb(${strokeColor.join(',')})`,
        fillStyle: `rgba(${fillColor.join(',')})`,
    };
};

const createStripedStyle = (context: CanvasRenderingContext2D): Partial<CanvasStyleItem> => {
    return {
        ...BASE_ITEM_STYLE,
        strokeStyle: `rgb(${GREY_DARK.join(',')})`,
        fillStyle: context.createPattern(stripedImage, 'repeat')!,
    };
};

const initCanvasStyles = async (context: CanvasRenderingContext2D): Promise<CanvasStyle> => {
    await loadStripedImage();
    return {
        polygons: {
            blue: createColoredStyle(BLUE, BLUE_ALPHA),
            green: createColoredStyle(GREEN, GREEN_ALPHA),
            orange: createColoredStyle(ORANGE, ORANGE_ALPHA),
            red: createColoredStyle(RED, RED_ALPHA),
            white: createColoredStyle(WHITE, WHITE_ALPHA),
            purple: createColoredStyle(PURPLE, PURPLE_ALPHA_LIGHT),
            link: createColoredStyle(BLUE, BLUE_ALPHA_LIGHT),
            highlighted: createColoredStyle(BLUE, BLUE_ALPHA),
            striped: createStripedStyle(context),
        },
    };
};

const applyStyle = (context: any, style: CanvasStyleItem) => {
    if (!context || !style) return;

    const entries = Object.entries(style as any);

    entries.forEach(([key, value]) => {
        if (typeof value === 'function') {
            context[key](value());
        } else {
            context[key] = value;
        }
    });
};

async function loadStripedImage() {
    stripedImage = new Image();
    stripedImage.src = getAssetsFolder(STRIPED_IMAGE_URL);

    await nextEvent(stripedImage, 'load');
}

export { initCanvasStyles, applyStyle };
